import {
  backendStatusSchema,
  mapFeaturesResponseSchema,
  mapConfigurationSchema,
  mercerSpheresResponseSchema,
  worldAnalysisResponseSchema,
  type BackendStatus,
  type MapConfiguration,
  type MapFeaturesResponse,
  type MercerSpheresResponse,
  type WorldAnalysisResponse,
} from '@satismap/shared';
import type { ZodType } from 'zod';
import { z } from 'zod';

const configuredBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? '').trim();
export const apiBaseUrl = configuredBaseUrl.replace(/\/$/u, '');

export class ApiError extends Error {
  public constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const authSessionSchema = z.object({
  enabled: z.boolean(),
  authenticated: z.boolean(),
});
const authResultSchema = z.object({ authenticated: z.boolean() });

export type AuthSession = z.infer<typeof authSessionSchema>;

function endpoint(path: string): string {
  return `${apiBaseUrl}${path}`;
}

async function readJson<T>(
  path: string,
  schema: ZodType<T>,
  init: RequestInit = {},
): Promise<T> {
  const response = await fetch(endpoint(path), {
    ...init,
    credentials: 'include',
    headers: { Accept: 'application/json', ...init.headers },
  });
  if (!response.ok) {
    let message = `API request failed with status ${response.status}`;
    try {
      const body = (await response.json()) as {
        error?: { message?: unknown };
      };
      if (typeof body.error?.message === 'string') {
        message = body.error.message;
      }
    } catch {
      // Keep the status-based message for non-JSON responses.
    }
    const error = new ApiError(message, response.status);
    if (response.status === 401 && typeof window !== 'undefined') {
      window.dispatchEvent(new Event('satismap:unauthorized'));
    }
    throw error;
  }
  return schema.parse((await response.json()) as unknown);
}

export function getBackendStatus(): Promise<BackendStatus> {
  return readJson('/api/status', backendStatusSchema);
}

export function getMercerSpheres(): Promise<MercerSpheresResponse> {
  return readJson('/api/mercer-spheres', mercerSpheresResponseSchema);
}

export function getMapFeatures(): Promise<MapFeaturesResponse> {
  return readJson('/api/map-features', mapFeaturesResponseSchema);
}

export function getMapConfiguration(): Promise<MapConfiguration> {
  return readJson('/api/map-config', mapConfigurationSchema);
}

export function getWorldAnalysis(): Promise<WorldAnalysisResponse> {
  return readJson('/api/statistics', worldAnalysisResponseSchema);
}

export function getEventStreamUrl(): string {
  return endpoint('/api/events');
}

export function getAuthSession(): Promise<AuthSession> {
  return readJson('/api/auth/session', authSessionSchema);
}

export function loginWithPassword(password: string): Promise<{ authenticated: boolean }> {
  return readJson('/api/auth/login', authResultSchema, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ password }),
  });
}

export function logoutSession(): Promise<{ authenticated: boolean }> {
  return readJson('/api/auth/logout', authResultSchema, { method: 'POST' });
}
